import type { Messages } from '../catalogue.en'

/**
 * Shell, identity and the messages every surface can reach: the app frame,
 *
 * Composed into the public catalogue by `catalogue.he.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const common: Partial<Messages> = {
  "clinicMaintenance.warning": "התחזוקה תתחיל ב־{deadline} (בעוד {seconds} שניות). נא לשמור את העבודה ולצאת מהמרפאה.",
  "clinicMaintenance.closed": "המרפאה נמצאת בתחזוקה",
  "clinicMaintenance.description": "הגישה תחזור עם סיום העבודות. שינויים שלא נשמרו נשארים בדף זה. אפשר לעבור למרפאה אחרת או לצאת מהחשבון.",
  "clinicMaintenance.recheck": "בדיקה חוזרת",
  "clinicMaintenance.choose": "מעבר למרפאה אחרת",
  "clinicMaintenance.developer": "תחזוקת המרפאה: גישת מפתח",
  'organizations.choose': 'בחירת ארגון',
  'organizations.notificationUnavailable': 'ההתראה אינה זמינה או שאין לך עוד גישה אליה.',
  'organizations.empty': 'אין ארגונים זמינים לחשבון זה.',
  'organizations.add': 'הוספת ארגון',
  'organizations.createTitle': 'ארגון חדש',
  'organizations.createUnsaved': 'פרטי ארגון חדש',
  'organizations.createOptionsFailed': 'יצירת ארגון אינה זמינה.',
  'organizations.createForbidden': 'אין לך הרשאה ליצור ארגונים.',
  'organizations.name': 'שם הארגון',
  'organizations.calendar': 'יומן עבודה',
  'organizations.timeZone': 'אזור זמן',
  'organizations.defaultLanguage': 'שפת ברירת מחדל',
  'organizations.createConflict': 'בקשה זו כבר נשמרה עם פרטים אחרים.',
  'organizations.createFailed': 'לא ניתן ליצור את הארגון.',
  'organizations.creating': 'יוצר',
  'organizations.createSubmit': 'יצירת ארגון',
  'organizations.createCancel': 'ביטול',
  'app.generationChanged': 'נתוני היישום הוחלפו. לא ניתן עוד לשמור שינויים מדף זה. יש להעתיק טקסט שלא נשמר לפני הטעינה מחדש.',
  'app.generationReload': 'טעינת היישום מחדש',
  'app.name': 'Terysling',
  'app.descriptor': 'סביבת עבודה קלינית',
  'app.skipToContent': 'דלג לתוכן הראשי',
  'app.documentTitle': '{page} — {brand}',
  'app.moduleWorkspace': 'סביבת עבודה של מודול',
  'app.loading': 'טוען',
  'app.sessionEnded': 'ההתחברות הסתיימה. חוזרים למסך הכניסה…',

  'app.developedBy': 'פותח על ידי',
  'app.developer': 'Mislab IL',

  'releaseVerification.cancel': 'המשך אימות הגרסה',
  'releaseVerification.finish': "סיום אימות הגרסה ופתיחת הגישה",
  'releaseVerification.confirm': "פעולה זו תפתח את סביבת הייצור למשתמשים פעילים ותפעיל עיבוד ברקע. היישום יופעל מחדש. טפסים שלא נשמרו בדף זה יישארו פתוחים.",
  'releaseVerification.confirmAction': "פתיחת סביבת הייצור",
  'releaseVerification.progress': "הגישה נפתחת. החיבור יתחדש אוטומטית…",
  'releaseVerification.loading': "קריאת מצב הגרסה…",
  'releaseVerification.unavailable': "לא ניתן לאשר את התוצאה. יש לבדוק את המצב לפני ניסיון נוסף.",
  'releaseVerification.forbidden': "רק מפתח רשאי לסיים את בדיקת הגרסה.",
  'releaseVerification.stale': "הגרסה השתנתה. יש לרענן את המצב לפני המשך הפעולה.",
  'releaseVerification.failed': "הפעולה נכשלה. יש לפנות למפעיל הגרסה; הנתונים הקיימים נשמרו.",
  'releaseVerification.checkStatus': "בדיקת מצב",
  'releaseVerification.refreshFailed': "הגרסה הופעלה, אך לא ניתן לרענן את ההפעלה. יש לבדוק שוב.",
  'releaseVerification.bannerLabel': 'בדיקת שחרור',
  'releaseVerification.eyebrow': 'בדיקת שחרור בפרודקשן',
  'releaseVerification.banner': 'בדיקת השחרור פעילה. כל דבר שתיצור או תשנה עכשיו עלול ללכת לאיבוד.',
  'releaseVerification.title': 'בדיקת שחרור בפרודקשן פעילה',
  'releaseVerification.description':
    'בזמן שבודקים את החלפת הפרודקשן, הכניסה מותרת רק למשתמשים עם הרשאת תחזוקת שחרור. אם השחרור יבוטל, עבודה שנוצרה בחלון הזה עלולה להימחק.',
  'releaseVerification.acknowledge': 'הבנתי',

  'language.label': 'שפה',
  'language.en': 'אנגלית',
  'language.he': 'עברית',
  'language.ru': 'רוסית',
  // Byte-identical to the English catalogue on purpose: an endonym is not
  // translated. See the note beside these ids in `en/common.ts`.
  'language.nativeEn': 'English',
  'language.nativeHe': 'עברית',
  'language.nativeRu': 'Русский',
  'language.saving': 'שומר שפה',
  'language.saveFailed': 'השפה לא נשמרה. נסה שוב.',

  'combobox.placeholder': 'לא נבחר',
  'combobox.search': 'חיפוש',
  'combobox.empty': 'אין התאמות',
  'combobox.searching': 'מחפש…',
  'combobox.chosenCount': 'נבחרו: {count}',
  'combobox.unchoose': 'הסרת {name}',

  'passwordInput.show': 'הצגת הסיסמה',
  'passwordInput.showSecret': 'הצגת הסוד',
  'passwordInput.policyHint': 'אורך הסיסמה בתווים: {min} עד {max}.',
  'passwordInput.requireDigit': 'יש לכלול לפחות ספרה אחת.',
  'passwordInput.requireUppercase': 'יש לכלול לפחות אות גדולה אחת.',
  'passwordInput.requireSpecial': 'יש לכלול לפחות תו מיוחד אחד (סימן פיסוק או סמל).',
  'passwordInput.policyUnavailable': 'לא ניתן לטעון את דרישות הסיסמה. יש לרענן את הדף כדי לנסות שוב.',

  'panel.close': 'סגירה',
  'sheet.done': 'סיום',

  'codePicker.tabs': 'היכן לחפש קוד',
  'codePicker.tabPersonal': 'הקודים שלי',
  'codePicker.searchLabel': 'חיפוש קוד',
  'codePicker.star': 'שמירת {name} בקודים שלי',
  'codePicker.unstar': 'הסרת {name} מהקודים שלי',
  'tree.expandNamed': 'הרחבת {name}',
  'tree.collapseNamed': 'כיווץ {name}',
  'tree.enterNamed': 'פתיחת {name}',
  'tree.back': 'חזרה',
  'tree.renameNamed': 'שם חדש עבור {name}',
  'codePicker.selectNamed': 'בחירת {name}',
  'codePicker.personalLoading': 'טוען…',
  'codePicker.personalLoadFailed': 'לא ניתן היה לקרוא את הקודים השמורים שלך.',
  'codePicker.personalEmpty': 'עדיין לא שמרת קודים.',
  'codePicker.personalEmptyDetail': 'סימון כוכב לקוד בלשונית הקטלוג יוסיף אותו לכאן.',
  'codePicker.personalEmptyNoGrant': 'החשבון שלך אינו יכול לשמור קודים ברשימה זו.',
  'codePicker.shortlist': 'מסומנים בכוכב',
  'codePicker.folderName': 'תיקייה חדשה',
  'codePicker.folderCreate': 'הוספת תיקייה',
  'codePicker.folderEmpty': 'התיקייה ריקה.',
  'codePicker.showLabel': 'הצגה',
  'codePicker.showAll': 'הכול',
  'codePicker.showFolders': 'תיקיות בלבד',
  'codePicker.showCodes': 'קודים בלבד',
  'codePicker.shortlistNoMatches': 'אף קוד שמור אינו תואם.',
  'codePicker.itemMenu': 'פעולות עבור {name}',
  'codePicker.menuMoveTo': 'העברה אל',
  'codePicker.menuRemove': 'הסרה מהקודים שלי',
  'codePicker.folderMenu': 'פעולות עבור התיקייה {name}',
  'codePicker.menuRename': 'שינוי שם התיקייה',
  'codePicker.menuDelete': 'מחיקת התיקייה והקודים שבה',
  'codePicker.folderNameTaken': 'כבר קיימת תיקייה בשם זה.',
  'codePicker.forbidden': 'אין לך הרשאה לשנות את הקודים השמורים שלך.',
  'codePicker.gone': 'התיקייה או הקוד אינם שמורים עוד.',
  'codePicker.invalid': 'לא ניתן לשמור קוד זה.',
  'codePicker.writeFailed': 'לא ניתן היה לשנות את הקודים השמורים. יש לנסות שוב.',

  'table.columnFilter': 'סינון {column}',
  'table.columnFilterReset': 'ניקוי הסינון של {column}',
  'table.columnFilterSearch': 'חיפוש',
  'table.columnFilterSelectVisible': 'בחירת המוצגים',
  'table.columnFilterClearVisible': 'ניקוי המוצגים',
  'table.columnFilterClear': 'ניקוי',
  'table.columnFilterNoValues': 'אין ערכים',
  'table.instantFilterPlaceholder': 'סינון לפי {columns}',
  'table.instantFilterLabel': 'סינון {table} לפי {columns}',
  'table.filterContains': 'מכיל',
  'table.criterionApply': 'החלה',
  'table.criterionHint': 'כל חלק מהערך, או /תבנית/ לביטוי רגולרי.',
  'table.criterionTooLong': 'הקריטריון ארוך מדי.',
  'table.criterionInvalidPattern': 'זהו אינו ביטוי רגולרי תקין.',
  'table.criterionNoMatch': 'אין ערך מתאים בעמודה זו.',
  'table.criterionOnDate': 'מותאם לתאריך בצורה YYYY-MM-DD.',
  'table.criterionOnInstant': 'מותאם לתאריך ולשעה כפי שמוצגים.',
  'table.criterionOnBytes': 'מותאם לגודל בבתים.',
  'table.criterionOnValues': 'ההחלה מסמנת כל ערך מתאים.',
  'table.criterionOnShownValue': 'מותאם לערך כפי שמוצג.',
  'table.rangeFrom': '{column} מ־',
  'table.rangeTo': '{column} עד',
  'table.resetFilters': 'ניקוי מסננים',
  'table.resetFiltersOf': 'ניקוי מסננים של {table}',
  'table.add': 'הוספה',
  'table.hideColumn': 'הסתרת {column}',
  'table.columns': 'עמודות',
  'table.columnsChoose': 'בחירת העמודות של {table}',
  'table.columnsChooseHidden': 'בחירת העמודות של {table} — חלקן מוסתרות',
  'table.columnsChooseFiltering': 'בחירת העמודות של {table} — עמודה מוסתרת מסננת אותה',
  'table.columnFixed': 'מוצגת תמיד',
  'table.columnLast': 'העמודה האחרונה בטבלה',
  'table.columnNarrowed': 'העמודה {column} מוסתרת ועדיין מסננת את הטבלה',
  'table.expandRow': 'הרחבת {name}',
  'table.collapseRow': 'צמצום {name}',
  'table.expandGroup': 'הרחבת הקבוצה {name}',
  'table.collapseGroup': 'צמצום הקבוצה {name}',
  'table.selectRow': 'בחירת {name}',
  'table.selectShown': 'בחירת השורות המוצגות',
  'table.clearSelection': 'ניקוי הבחירה',
  'table.filters': 'מסננים',
  'table.filterSheet': 'מסננים של {table}',
  'table.rowActions': 'פעולות עבור {name}',
  'table.bulkRefused': 'השרת סירב ל־{names}.',
  'table.bulkRefusedMore': 'השרת סירב ל־{names} ועוד {count}.',
  'table.bulkRefusedDetail': 'כל שורה שלא בוצעה עדיין מסומנת.',
  'table.notSet': 'לא הוגדר',
  'table.pageRow': 'שורה {from}',
  'table.pageRows': 'שורות {from}–{to}',
  'table.pageNumber': 'עמוד {page}',
  'table.previousPage': 'הקודם',
  'table.nextPage': 'הבא',
  'table.pagesBelow': 'עמודים, מתחת לטבלה {table}',
  'table.pageSizeBelow': 'שורות בעמוד, מתחת לטבלה {table}',
  'table.noMatches': 'אין שורות מתאימות לסינונים',
  'table.noMatchesDetail': 'הרחב או אפס את סינוני העמודות.',
  'table.assistantOpen': 'תיאור מה לחפש ב{table}',
  'table.assistantTitle': 'תיאור מה לחפש',
  'table.assistantIntro': 'נסח מה אתה מחפש, ועמודות {table} יסוננו עבורך.',
  'table.assistantPrivacy': 'נשלחות רק כותרות העמודות והמילים שלך. שום שורה, ערך או מנייה אינם יוצאים מהמרפאה.',
  'table.assistantLabel': 'מה אתה מחפש?',
  'table.assistantPlaceholder': 'נרשמו החודש, השם מכיל לוין',
  'table.assistantAsk': 'הצעת סינונים',
  'table.assistantRetry': 'שאילה מחדש',
  'table.assistantApply': 'החלת הסינונים',
  'table.assistantClose': 'סגירה',
  'table.assistantWorking': 'מחשב את הסינונים…',
  'table.assistantReady': 'ההחלה מחליפה את הסינונים הפעילים.',
  'table.assistantNoFilters': 'אין בטבלה זו עמודה שאפשר לצמצם לפי זה.',
  'table.assistantContains': 'מכיל',
  'table.assistantFrom': 'מ־',
  'table.assistantTo': 'עד',
  'table.assistantUnmatched': 'סינוני עמודות אינם יכולים לבקש:',
  'table.assistantKeyMissing': 'לחשבון זה לא הוגדר מפתח OpenAI.',
  'table.assistantForbidden': 'חשבון זה אינו רשאי להריץ בקשות בינה מלאכותית.',
  'table.assistantFailed': 'לא ניתן היה להציע סינונים. נסה שוב.',

  'shell.resizeContext': 'רוחב חלונית ההקשר',

  'appearance.label': 'מראה',
  'appearance.theme': 'ערכת נושא',
  'appearance.mode': 'מצב',
  'appearance.light': 'בהיר',
  'appearance.dark': 'כהה',
  'appearance.followTelegram': 'לפי Telegram',
  'appearance.scale': 'גודל טקסט',
  // Digits and a per-cent sign read the same in both directions; the number is
  // the whole label, so there is nothing here to translate.
  'appearance.scaleValue': '{percent}%',
  'appearance.density': 'צפיפות',
  'appearance.densityCompact': 'צפוף',
  'appearance.densityCozy': 'נוח',
  'appearance.densityRelaxed': 'מרווח',
  // A proper name, kept as-is like `app.name`.
  'appearance.themeTerys': 'Terys',
  'appearance.themeEmerald': 'ברקת',
  'appearance.themeAmber': 'ענבר',
  'appearance.themeGraphite': 'גרפיט',
  'appearance.themeSmartomica': 'Smartomica',
  'appearance.themeSmartomicaBrand': 'מותג Smartomica',

  'login.title': 'כניסה',
  'login.subtitle': 'היכנס למרחב העבודה של המרפאה.',
  'login.username': 'שם משתמש',
  'login.password': 'סיסמה',
  'login.submit': 'כניסה',
  'login.submitting': 'מתחבר',
  'login.usernameRequired': 'הזן שם משתמש.',
  'login.passwordRequired': 'הזן סיסמה.',
  'login.telegramNotLinked': "יש לקשר תחילה את Telegram לחשבון Terysling בפרופיל. אפשר להתחבר עם סיסמה למטה.",
  'login.telegramInactive': "הכניסה דרך Telegram אינה זמינה לחשבון הזה. יש לפנות למנהל המרפאה.",
  'login.telegramExpired': "אישור הכניסה דרך Telegram פג או כבר נוצל. יש ללחוץ שוב על כפתור האירוע ב־Telegram או להתחבר למטה.",
  'login.telegramInvalid': "לא ניתן לאמת את הכניסה דרך Telegram. יש ללחוץ שוב על כפתור האירוע ב־Telegram או להתחבר למטה.",
  'login.failed': 'ההתחברות נכשלה. בדוק את שם המשתמש והסיסמה.',
  'login.maintenanceRestricted': 'הגישה מוגבלת במהלך תחזוקה. נסו להתחבר שוב לאחר סיום התחזוקה.',
  'login.throttled': 'יותר מדי ניסיונות התחברות. נסה שוב מאוחר יותר.',
  'login.unavailable': 'השירות אינו זמין כעת. נסה שוב מאוחר יותר.',

  'shell.primaryNavigation': 'ראשי',
  'shell.navCollapse': 'כיווץ הניווט',
  'shell.navExpand': 'הרחבת הניווט',
  'shell.navShow': 'הצגת הניווט',
  'shell.navHide': 'הסתרת הניווט',
  'shell.navDrawer': 'ניווט',
  'shell.contextHide': 'הסתרת חלונית ההקשר',
  'shell.contextShow': 'הצגת חלונית ההקשר',
  'shell.accountRegion': 'חשבון וסביבת עבודה',
  'shell.activeClinic': 'ארגון פעיל',
  'shell.clinicSwitchFailed': 'לא ניתן היה להחליף את המרפאה הפעילה.',
  'shell.organizationCurrent': 'נוכחי',
  'shell.organizationSwitchClose': 'סגירה',
  'shell.organizationContextChanged': 'הקשר הארגון השתנה. יש לרענן אותו לפני המשך העבודה.',
  'shell.organizationContextRefresh': 'רענון הקשר הארגון',
  'shell.elevation': 'הרשאת מפתח מוגברת',
  'shell.elevationActive': 'הרשאת מפתח מוגברת פעילה',
  'shell.elevationOff': 'ההרשאה המוגברת אינה פעילה',
  'shell.elevationEnds': 'מסתיימת',
  'shell.elevationNoExpiry': 'רק בסיום ידני',
  'shell.elevationDeactivate': 'סיום הרשאה מוגברת',
  'shell.elevationDeactivateFailed': 'לא ניתן היה לסיים את הרשאת המפתח המוגברת.',
  'shell.elevationReason': 'סיבה להרשאת מפתח מוגברת',
  'shell.elevationTemporary': 'הרשאה מוגברת לשעה',
  'shell.elevationPersistent': 'השארת הרשאה מוגברת בפיתוח',
  'shell.elevationActivateFailed': 'לא ניתן היה להפעיל הרשאת מפתח מוגברת.',
  'shell.signOut': 'התנתקות',
  'shell.signingOut': 'מתנתק',
  'shell.sessionExpired': 'ההפעלה הסתיימה. היכנס שוב.',

  'shell.unsavedTitle': 'העבודה הזו לא נשמרה',
  'shell.unsavedBodyOne': 'יציאה עכשיו תמחק עבודה שהשרת מעולם לא קיבל:',
  'shell.unsavedBodyMany': 'יציאה עכשיו תמחק {count} פריטי עבודה שהשרת מעולם לא קיבל:',
  'shell.unsavedStay': 'הישאר בדף',
  'shell.unsavedDiscard': 'מחק וצא',

  'shell.contextPanel': 'חלונית הקשר',

  'notFound.title': 'הדף לא נמצא',
  'notFound.detail': 'הדף הזה אינו קיים.',
  'notFound.back': 'מעבר ללוח הבקרה',

  'error.invalidReadRequest': 'השרת דחה את הבקשה לטעינת הדף',
  'error.invalidReadRequestDetail': 'בקש ממנהל לבדוק את כתובת הדף ואת הבקשה.',
  'error.title': 'משהו השתבש',
  'error.detail': 'לא ניתן היה לטעון את הדף.',
  'error.retry': 'נסה שוב',
  'error.forbidden': 'אין לך גישה לתוכן הזה',
  'error.forbiddenDetail': 'פנה למנהל המערכת אם הגישה נדרשת.',
  'error.invalidResponse': 'תגובת השרת חסרה פרטים.',

  'error.notRefreshed': 'לא רוענן',
  'error.notRefreshedDetail': 'מוצג המידע שהשרת אישר לאחרונה. ייתכן שהשתנה מאז.',

  'nav.patients': 'מטופלים',
  'nav.patientsDescription': 'רישום, תיקים ומפגשים',

  'nav.schedule': 'יומן',
  'nav.scheduleDescription': 'היום והתורים שבו',

  'nav.tasksDescription': 'עבודת מרפאה ומעקבים',

  'nav.leads': 'לידים',
  'nav.leadsDescription': 'פניות חדשות וקליטת מטופלים',

  "nav.controlCenter": "ניהול המרפאה",
  "nav.controlCenterDescription": "ארגון, עובדים, טפסים וחיבורים",

  'nav.backToDashboard': 'חזרה ללוח הבקרה',
  'nav.backToPatients': 'חזרה למטופלים',
  'nav.backToPatient': 'חזרה למטופל',
  'nav.backToSchedule': 'חזרה ליומן',
  'nav.backToControlCenter': 'חזרה לניהול המרפאה',
  'nav.backToHelp': 'חזרה לעזרה',

  'quickAccess.label': 'גישה מהירה',
  'quickAccess.customize': 'התאמת הגישה המהירה',
  'quickAccess.hint':
    'החלקים שנבחרו נשארים במרחק לחיצה אחת מכל מסך. גררו אריח מלוח ניווט, או מטופל מרשימת המטופלים, ושחררו בכל מקום ברצועה העליונה — או לחצו על הסיכה שלצדו: גם חלק של מטופל או של רשומה. המקום שבו תשחררו הוא המקום שבו הוא יעמוד, וגרירת סימנייה לאורך השורה מזיזה אותה.',
  'quickAccess.groupWorkspace': 'סביבת עבודה',
  'quickAccess.groupProfile': 'פרופיל',
  'quickAccess.groupChosen': 'בשורה',
  'quickAccess.pin': 'הוספת {name} לגישה המהירה',
  'quickAccess.unpin': 'הסרת {name} מהגישה המהירה',
  'quickAccess.unpinUnavailable': 'הסרת הסימנייה שאינה זמינה מהגישה המהירה',
  'quickAccess.dropHint': 'שחררו כדי להוסיף לגישה המהירה',
  'quickAccess.moveHint': 'שחררו כדי להזיז לכאן',
  'quickAccess.moveEarlier': 'הזזת {name} קרוב יותר לתחילת הגישה המהירה',
  'quickAccess.moveLater': 'הזזת {name} קרוב יותר לסוף הגישה המהירה',
  'quickAccess.bookmarkName': '{within}: {label}',
  'quickAccess.recordName': '{subject}: {label}',
  'quickAccess.recordViewName': '{subject}: {within}, {label}',
  'quickAccess.entrySubject': '{name}, רישום {code}',
  'quickAccess.entryOnly': 'רישום {code}',
  'quickAccess.unavailable': 'אינו זמין עוד',
  'common.unknownValue': 'ערך לא מוכר',
  /* אופן כתיבת מספר גרסה, במקום אחד. */
  'common.version': 'v{version}',
  /* גרסה של רשומה, במקום אחד. */
  'common.revision': 'גרסה',
  'common.revisionNumber': 'גרסה {revision}',
  'common.revisionHistory': 'היסטוריית גרסאות',
  /* יחידת גודל הקובץ. העברית משתמשת בקיצור הלטיני, כמו שאר הטקסטים כאן. */
  'common.bytes': '{size} B',
  'common.kilobytes': '{size} KB',
  'common.megabytes': '{size} MB',
  'time.invalidLocal': 'יש לבחור תאריך ושעה מקומיים שקיימים באזור הזמן של המרפאה.',
  'coding.loading': 'טוען התאמות מהמילון',
  'coding.empty': 'לא נמצאו התאמות במילון',
  'coding.failed': 'לא ניתן לחפש במילון.',
}
